import {
  Flame,
  UtensilsCrossed,
  Repeat,
  Info,
  Plus,
  BookOpen,
  Beef,
  Leaf,
  Apple,
} from "lucide-react";

const GOAL_CONFIG = {
  bulking: {
    label: "Bulking",
    tone: "text-orange-400",
    bg: "bg-orange-500/15 border-orange-500/25",
    icon: Beef,
  },
  cutting: {
    label: "Cutting",
    tone: "text-rose-400",
    bg: "bg-rose-500/15 border-rose-500/25",
    icon: Flame,
  },
  clean_eating: {
    label: "Clean Eating",
    tone: "text-amber-400",
    bg: "bg-amber-500/15 border-amber-500/25",
    icon: Leaf,
  },
  maintenance: {
    label: "Maintenance",
    tone: "text-emerald-400",
    bg: "bg-emerald-500/15 border-emerald-500/25",
    icon: Apple,
  },
};

export default function AssignedMealPlanCard({
  plan,
  onSwapPlan,
  onViewPlanDetails,
  onOpenLibrary,
}) {
  if (!plan) {
    return (
      <div
        className="p-5 rounded-2xl bg-[#071326] border border-white/10 text-center space-y-3"
        data-testid="no-assigned-meal-plan-card"
      >
        <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 mx-auto">
          <UtensilsCrossed className="w-5 h-5 text-sky-400" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white">No Meal Plan Assigned</h3>
          <p className="text-xs text-slate-400 max-w-xs mx-auto mt-0.5">
            Choose a nutrition plan to start planning your meals for this day.
          </p>
        </div>
        <div className="flex items-center justify-center gap-2 pt-1">
          <button
            type="button"
            onClick={onOpenLibrary}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs shadow-md active:scale-95 transition cursor-pointer"
          >
            <BookOpen className="w-3.5 h-3.5" />
            <span>Browse Meal Plans</span>
          </button>
          <button
            type="button"
            onClick={onSwapPlan}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium text-xs transition cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5 text-sky-400" />
            <span>Assign Plan</span>
          </button>
        </div>
      </div>
    );
  }

  const goal = (plan.goal || "").toLowerCase();
  const goalConfig = GOAL_CONFIG[goal] || {
    label: plan.goal || "Nutrition Plan",
    tone: "text-sky-400",
    bg: "bg-sky-500/15 border-sky-500/25",
    icon: UtensilsCrossed,
  };
  const GoalIcon = goalConfig.icon;
  const mealCount = Array.isArray(plan.meals) ? plan.meals.length : 0;

  return (
    <div
      className="p-4 rounded-2xl bg-gradient-to-r from-[#08152B] to-[#040C1A] border border-white/10 shadow-md relative overflow-hidden"
      data-testid="assigned-meal-plan-card"
    >
      {/* Decorative subtle blue radial glow */}
      <div
        className="absolute -right-6 -bottom-6 w-32 h-32 rounded-full pointer-events-none opacity-20"
        style={{
          background: "radial-gradient(circle, rgba(56,189,248,0.4) 0%, transparent 70%)",
        }}
      />

      <div className="flex items-start justify-between gap-3 relative z-10">
        <div className="min-w-0 flex-1">
          {/* Header Tag */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold tracking-widest text-sky-400 uppercase">
              Assigned Meal Plan
            </span>
            {plan.goal && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border ${goalConfig.bg} ${goalConfig.tone}`}
              >
                <GoalIcon className="w-2.5 h-2.5" />
                <span>{goalConfig.label}</span>
              </span>
            )}
          </div>

          {/* Large Title */}
          <h2 className="text-base font-extrabold text-white truncate leading-tight">
            {plan.name}
          </h2>

          {/* Supporting Information */}
          <div className="flex items-center gap-3 text-xs text-slate-300 mt-1">
            {plan.daily_calories ? (
              <span className="flex items-center gap-1 font-semibold text-orange-400">
                <Flame className="w-3.5 h-3.5" />
                {plan.daily_calories.toLocaleString()} kcal target
              </span>
            ) : null}
            <span className="flex items-center gap-1 text-slate-400">
              <UtensilsCrossed className="w-3.5 h-3.5 text-sky-400" />
              {mealCount} meal{mealCount !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1 shrink-0 pt-0.5">
          <button
            type="button"
            onClick={onViewPlanDetails}
            className="p-2 rounded-xl bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-white/10 active:scale-95 transition cursor-pointer"
            title="View Plan Details"
            aria-label="View Plan Details"
          >
            <Info className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onSwapPlan}
            className="p-2 rounded-xl bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-white/10 active:scale-95 transition cursor-pointer"
            title="Change Plan"
            aria-label="Change Plan"
          >
            <Repeat className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
